import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { searchNews } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { useScrollObserver } from "@/hooks/useScrollObserver";
import InfiniteNewsFeed from "@/components/InfiniteNewsFeed";
import SEO from "@/components/SEO";

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const { user } = useAuth();
  const country = (user?.country || "US").toUpperCase();

  const fetchFn = useCallback(
    (page) => searchNews(q, country, page, 12),
    [q, country]
  );

  const feed = useInfiniteFeed({
    fetchFn,
    feedKey: `search_${q}_${country}`,
    getCacheKey: null,
    ttlMs: 0,
  });

  const sentinelRef = useScrollObserver(feed.loadMore);

  const resultSummary = feed.loading
    ? "Searching…"
    : `${feed.items.length} result${feed.items.length !== 1 ? "s" : ""} loaded`;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-6">
      <SEO
        title={q ? `Search: ${q}` : "Search AI News"}
        description={
          q
            ? `TrendMing search results for "${q}" — latest AI news, trending stories and analysis.`
            : "Search the latest AI news and trending stories on TrendMing."
        }
        noindex={true}
      />

      <section className="py-10 md:py-14 border-b border-slate-200" data-testid="search-header">
        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600 mb-2">Search results</p>
        <h1 className="font-bold text-3xl md:text-5xl text-slate-900 mt-1">
          "{q}"
        </h1>
        <p className="mt-3 text-slate-400 text-sm" aria-live="polite">
          {resultSummary}
        </p>
      </section>

      <section className="py-10" data-testid="search-results-section">
        <InfiniteNewsFeed
          feed={feed}
          sentinelRef={sentinelRef}
          emptyMessage={q ? `No results for "${q}".` : "Enter a search term above."}
          testId="search-grid"
        />
      </section>
    </div>
  );
}
